import { Fragment, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { billingApi } from "@/api/billing";
import { paymentApi } from "@/api/payment";
import { Bill, PaymentMethod } from "@/types/types";
import { ChevronDown, ChevronRight, FileDown, IndianRupee, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useChainReportHospitalScope } from "@/hooks/useChainReportHospitalScope";
import { ReportHospitalFilter } from "@/components/reports/ReportHospitalFilter";

type BillItemRow = {
  id: string;
  itemType: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
};

const PAYMENT_METHODS: PaymentMethod[] = [
  PaymentMethod.CASH,
  PaymentMethod.UPI,
  PaymentMethod.CARD,
  PaymentMethod.BANK_TRANSFER,
  PaymentMethod.CHEQUE,
  PaymentMethod.INSURANCE,
];

function billDueAmount(bill: Bill): number {
  const due = Number(bill.dueAmount);
  if (Number.isFinite(due)) return Math.max(0, Math.round(due * 100) / 100);
  return Math.max(
    0,
    Math.round((Number(bill.totalAmount || 0) - Number(bill.paidAmount || 0)) * 100) / 100
  );
}

function canMarkBillPaid(bill: Bill): boolean {
  const status = String(bill.status || "");
  if (status === "CANCELLED" || status === "PAID") return false;
  return billDueAmount(bill) > 0.005;
}

export default function PharmacySalesReportPage() {
  const { user } = useAuth();
  const {
    showHospitalFilter,
    hospitalOptions,
    selectedHospitalId,
    setSelectedHospitalId,
    reportHospitalId,
    isSuperAdmin,
  } = useChainReportHospitalScope({ defaultScope: "consolidated" });
  const queryClient = useQueryClient();
  const canManageItems =
    user?.role === "HOSPITAL_ADMIN" || user?.role === "SUPER_ADMIN";

  const todayIso = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(todayIso);
  const [to, setTo] = useState(todayIso);
  const [expandedBillId, setExpandedBillId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<{
    billId: string;
    itemId: string;
    quantity: string;
    unitPrice: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ billId: string; itemId: string; label: string } | null>(
    null
  );
  const [deleteBillTarget, setDeleteBillTarget] = useState<Bill | null>(null);
  const [deleteBillConfirm, setDeleteBillConfirm] = useState("");
  const [downloadingBillId, setDownloadingBillId] = useState<string | null>(null);
  const [payTarget, setPayTarget] = useState<Bill | null>(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [payNotes, setPayNotes] = useState("");

  const queryKey = useMemo(
    () => ["pharmacy-sales", from, to, reportHospitalId ?? "self"],
    [from, to, reportHospitalId]
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await billingApi.getBillsByHospital({
        startDate: from,
        endDate: to,
        page: 1,
        limit: 200,
        ...(reportHospitalId ? { hospitalId: reportHospitalId } : {}),
      });
      const payload = res.data?.data as { bills?: Bill[] } | Bill[] | undefined;
      const bills = (Array.isArray(payload) ? payload : payload?.bills ?? []) as Bill[];
      return bills.filter(
        (b) =>
          Array.isArray(b.billItems) &&
          b.billItems.some((it) => it.itemType === "MEDICINE")
      );
    },
  });

  const updateItemMut = useMutation({
    mutationFn: async (payload: {
      billId: string;
      itemId: string;
      quantity: number;
      unitPrice: number;
    }) =>
      billingApi.updateBillItem(payload.billId, payload.itemId, {
        quantity: payload.quantity,
        unitPrice: payload.unitPrice,
      }),
    onSuccess: () => {
      toast.success("Bill item updated");
      setEditingItem(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err?.response?.data?.message || "Failed to update item");
    },
  });

  const deleteItemMut = useMutation({
    mutationFn: async (payload: { billId: string; itemId: string }) =>
      billingApi.deleteBillItem(payload.billId, payload.itemId),
    onSuccess: (res) => {
      const msg =
        (res as { data?: { message?: string } })?.data?.message || "Bill item removed";
      toast.success(msg);
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err?.response?.data?.message || "Failed to delete item");
    },
  });

  const deleteBillMut = useMutation({
    mutationFn: async (bill: Bill) =>
      billingApi.deleteBill(bill.id, { confirmBillNumber: bill.billNumber }),
    onSuccess: (res) => {
      const msg =
        (res as { data?: { message?: string } })?.data?.message ||
        (res as { message?: string })?.message ||
        "Bill deleted";
      toast.success(msg);
      setDeleteBillTarget(null);
      setDeleteBillConfirm("");
      setExpandedBillId(null);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err?.response?.data?.message || "Failed to delete bill");
    },
  });

  const markPaidMut = useMutation({
    mutationFn: async (payload: {
      billId: string;
      amount: number;
      paymentMethod: PaymentMethod;
      notes?: string;
    }) => paymentApi.processPayment(payload),
    onSuccess: (_res, vars) => {
      const dueLeft =
        payTarget != null
          ? Math.max(0, billDueAmount(payTarget) - vars.amount)
          : 0;
      toast.success(
        dueLeft > 0.005
          ? `Payment of ₹${vars.amount.toFixed(2)} recorded`
          : "Payment recorded — bill marked paid"
      );
      setPayTarget(null);
      setPayAmount("");
      setPayNotes("");
      setPayMethod(PaymentMethod.CASH);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      toast.error(err?.response?.data?.message || "Failed to record payment");
    },
  });

  const openMarkPaid = (bill: Bill) => {
    const due = billDueAmount(bill);
    setPayTarget(bill);
    setPayAmount(due.toFixed(2));
    setPayMethod(PaymentMethod.CASH);
    setPayNotes("");
  };

  const submitMarkPaid = () => {
    if (!payTarget) return;
    const due = billDueAmount(payTarget);
    const amount = parseFloat(payAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    if (amount > due + 0.005) {
      toast.error(`Amount cannot exceed due (₹${due.toFixed(2)})`);
      return;
    }
    markPaidMut.mutate({
      billId: payTarget.id,
      amount: Math.round(amount * 100) / 100,
      paymentMethod: payMethod,
      notes: payNotes.trim() || undefined,
    });
  };

  const bills = data ?? [];
  const totalValue = bills.reduce((sum, b) => sum + (b.totalAmount || 0), 0);

  const medicineItemsForBill = (bill: Bill): BillItemRow[] =>
    (bill.billItems ?? []).filter((it) => it.itemType === "MEDICINE") as BillItemRow[];

  const handleDownloadBillPdf = async (bill: Bill) => {
    setDownloadingBillId(bill.id);
    try {
      const response = await billingApi.exportBillPDF(bill.id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Bill-${bill.billNumber || bill.id}.pdf`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success("Bill PDF downloaded");
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(message || "Failed to download bill PDF");
    } finally {
      setDownloadingBillId(null);
    }
  };

  const startEditItem = (billId: string, item: BillItemRow) => {
    setEditingItem({
      billId,
      itemId: item.id,
      quantity: String(item.quantity ?? 1),
      unitPrice: String(item.unitPrice ?? 0),
    });
  };

  const saveEditItem = () => {
    if (!editingItem) return;
    const quantity = parseInt(editingItem.quantity, 10);
    const unitPrice = parseFloat(editingItem.unitPrice);
    if (!Number.isFinite(quantity) || quantity < 1) {
      toast.error("Enter a valid quantity");
      return;
    }
    if (!Number.isFinite(unitPrice) || unitPrice < 0) {
      toast.error("Enter a valid unit price");
      return;
    }
    updateItemMut.mutate({
      billId: editingItem.billId,
      itemId: editingItem.itemId,
      quantity,
      unitPrice,
    });
  };

  const colSpan = canManageItems ? 7 : 6;

  const billDeleteConfirmOk =
    deleteBillTarget?.billNumber &&
    deleteBillConfirm.trim().toUpperCase() === String(deleteBillTarget.billNumber).trim().toUpperCase();

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Pharmacy Bills Report</CardTitle>
          {canManageItems && (
            <p className="text-sm text-muted-foreground">
              Expand a bill to edit or delete pharmacy line items (including on paid bills). Use{" "}
              <strong>Mark paid</strong> for unpaid or partly paid bills, or <strong>Delete bill</strong> to
              remove the entire bill — medicines return to inventory and reports update automatically.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            {showHospitalFilter ? (
              <ReportHospitalFilter
                hospitals={hospitalOptions}
                value={selectedHospitalId}
                onChange={setSelectedHospitalId}
                isSuperAdmin={isSuperAdmin}
              />
            ) : null}
            <div>
              <label className="block text-xs text-gray-500 mb-1">From date</label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To date</label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
            </div>
            <Button type="button" onClick={() => refetch()} disabled={isLoading}>
              {isLoading ? "Loading..." : "Apply"}
            </Button>
            <div className="ml-auto text-sm">
              <span className="font-medium">Total value:</span>{" "}
              <span className="font-semibold">₹{totalValue.toFixed(2)}</span>
            </div>
          </div>

          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {canManageItems && <TableHead className="w-8" />}
                  <TableHead>Bill no.</TableHead>
                  <TableHead>Bill date</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead className="text-right">Amount (₹)</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                  <TableHead className="text-right">{canManageItems ? "Actions" : "Bill"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="text-center py-6 text-gray-500">
                      Loading bills...
                    </TableCell>
                  </TableRow>
                ) : bills.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} className="text-center py-6 text-gray-500">
                      No bills found for this range.
                    </TableCell>
                  </TableRow>
                ) : (
                  bills.map((b) => {
                    const expanded = expandedBillId === b.id;
                    const medicineItems = medicineItemsForBill(b);
                    const due = billDueAmount(b);
                    const showMarkPaid = canManageItems && canMarkBillPaid(b);
                    return (
                      <Fragment key={b.id}>
                        <TableRow>
                          {canManageItems && (
                            <TableCell className="p-2">
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => setExpandedBillId(expanded ? null : b.id)}
                                title={expanded ? "Collapse" : "Show line items"}
                              >
                                {expanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                            </TableCell>
                          )}
                          <TableCell className="font-mono text-xs">{b.billNumber}</TableCell>
                          <TableCell className="text-xs">
                            {b.billDate
                              ? new Date(b.billDate as unknown as string).toLocaleDateString("en-IN")
                              : "-"}
                          </TableCell>
                          <TableCell className="text-xs">{b.patient?.name ?? "—"}</TableCell>
                          <TableCell className="text-right font-medium">
                            ₹{(b.totalAmount || 0).toFixed(2)}
                            {due > 0.005 && (
                              <div className="text-[11px] font-normal text-amber-700">
                                Due ₹{due.toFixed(2)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-xs">
                            {b.status?.toString().replace(/_/g, " ")}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex flex-wrap items-center justify-end gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={downloadingBillId === b.id}
                                onClick={() => handleDownloadBillPdf(b)}
                              >
                                {downloadingBillId === b.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <FileDown className="h-4 w-4 mr-1" />
                                    Download
                                  </>
                                )}
                              </Button>
                              {showMarkPaid && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="border-emerald-600/40 text-emerald-800 hover:bg-emerald-50"
                                  disabled={markPaidMut.isPending}
                                  onClick={() => openMarkPaid(b)}
                                >
                                  <IndianRupee className="h-4 w-4 mr-1" />
                                  Mark paid
                                </Button>
                              )}
                              {canManageItems && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="text-destructive border-destructive/40 hover:bg-destructive/10"
                                  disabled={deleteBillMut.isPending}
                                  onClick={() => {
                                    setDeleteBillConfirm("");
                                    setDeleteBillTarget(b);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Delete bill
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {canManageItems && expanded && (
                          <TableRow className="bg-slate-50/80">
                            <TableCell colSpan={colSpan} className="p-3">
                              <div className="text-xs font-medium text-muted-foreground mb-2">
                                Pharmacy line items ({medicineItems.length})
                              </div>
                              <table className="min-w-full text-xs border rounded bg-white">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="p-2 text-left">Description</th>
                                    <th className="p-2 text-right">Qty</th>
                                    <th className="p-2 text-right">Unit price (₹)</th>
                                    <th className="p-2 text-right">Line total (₹)</th>
                                    <th className="p-2 text-right w-28">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {medicineItems.map((item) => {
                                    const isEditing =
                                      editingItem?.billId === b.id && editingItem?.itemId === item.id;
                                    return (
                                      <tr key={item.id} className="border-t">
                                        <td className="p-2">{item.description}</td>
                                        <td className="p-2 text-right">
                                          {isEditing ? (
                                            <Input
                                              type="number"
                                              min={1}
                                              step={1}
                                              className="h-8 w-16 ml-auto text-right"
                                              value={editingItem.quantity}
                                              onChange={(e) =>
                                                setEditingItem((prev) =>
                                                  prev ? { ...prev, quantity: e.target.value } : prev
                                                )
                                              }
                                            />
                                          ) : (
                                            item.quantity
                                          )}
                                        </td>
                                        <td className="p-2 text-right">
                                          {isEditing ? (
                                            <Input
                                              type="number"
                                              min={0}
                                              step="0.01"
                                              className="h-8 w-24 ml-auto text-right"
                                              value={editingItem.unitPrice}
                                              onChange={(e) =>
                                                setEditingItem((prev) =>
                                                  prev ? { ...prev, unitPrice: e.target.value } : prev
                                                )
                                              }
                                            />
                                          ) : (
                                            item.unitPrice.toFixed(2)
                                          )}
                                        </td>
                                        <td className="p-2 text-right font-medium">
                                          {item.totalPrice.toFixed(2)}
                                        </td>
                                        <td className="p-2 text-right">
                                          {isEditing ? (
                                            <div className="flex justify-end gap-1">
                                              <Button
                                                type="button"
                                                size="sm"
                                                onClick={saveEditItem}
                                                disabled={updateItemMut.isPending}
                                              >
                                                Save
                                              </Button>
                                              <Button
                                                type="button"
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setEditingItem(null)}
                                              >
                                                Cancel
                                              </Button>
                                            </div>
                                          ) : (
                                            <div className="flex justify-end gap-1">
                                              <Button
                                                type="button"
                                                size="icon"
                                                variant="ghost"
                                                className="h-8 w-8"
                                                onClick={() => startEditItem(b.id, item)}
                                                title="Edit item"
                                              >
                                                <Pencil className="h-4 w-4" />
                                              </Button>
                                              <Button
                                                type="button"
                                                size="icon"
                                                variant="ghost"
                                                className="h-8 w-8 text-red-600 hover:text-red-700"
                                                onClick={() =>
                                                  setDeleteTarget({
                                                    billId: b.id,
                                                    itemId: item.id,
                                                    label: item.description,
                                                  })
                                                }
                                                title="Delete item"
                                              >
                                                <Trash2 className="h-4 w-4" />
                                              </Button>
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {medicineItems.length === 0 && (
                                    <tr>
                                      <td colSpan={5} className="p-3 text-center text-muted-foreground">
                                        No medicine line items on this bill.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!deleteTarget} onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove bill line item?</DialogTitle>
            <DialogDescription>
              Delete <strong>{deleteTarget?.label}</strong> from this pharmacy bill? The bill total will be
              recalculated and remaining quantity will return to inventory. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteItemMut.isPending}
              onClick={() => {
                if (deleteTarget) {
                  deleteItemMut.mutate({
                    billId: deleteTarget.billId,
                    itemId: deleteTarget.itemId,
                  });
                }
              }}
            >
              {deleteItemMut.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!deleteBillTarget}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setDeleteBillTarget(null);
            setDeleteBillConfirm("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete entire pharmacy bill?</DialogTitle>
            <DialogDescription>
              Permanently delete bill <strong>{deleteBillTarget?.billNumber}</strong> for{" "}
              <strong>{deleteBillTarget?.patient?.name ?? "this patient"}</strong>? All payments and line
              items are removed; medicines return to inventory; reports and books adjust automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-sm text-muted-foreground">
              Type the bill number exactly to confirm:
            </label>
            <Input
              value={deleteBillConfirm}
              onChange={(e) => setDeleteBillConfirm(e.target.value)}
              placeholder={deleteBillTarget?.billNumber ?? ""}
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteBillTarget(null);
                setDeleteBillConfirm("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700"
              disabled={!billDeleteConfirmOk || deleteBillMut.isPending}
              onClick={() => {
                if (deleteBillTarget && billDeleteConfirmOk) {
                  deleteBillMut.mutate(deleteBillTarget);
                }
              }}
            >
              {deleteBillMut.isPending ? "Deleting…" : "Delete bill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!payTarget}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setPayTarget(null);
            setPayAmount("");
            setPayNotes("");
            setPayMethod(PaymentMethod.CASH);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark pharmacy bill paid</DialogTitle>
            <DialogDescription>
              Record payment for <strong>{payTarget?.billNumber}</strong>
              {payTarget?.patient?.name ? (
                <>
                  {" "}
                  ({payTarget.patient.name})
                </>
              ) : null}
              . Due: ₹{payTarget ? billDueAmount(payTarget).toFixed(2) : "0.00"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Amount (₹)</label>
              <Input
                type="number"
                min={0.01}
                step="0.01"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Payment method</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value as PaymentMethod)}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Notes (optional)</label>
              <Input
                value={payNotes}
                onChange={(e) => setPayNotes(e.target.value)}
                placeholder="e.g. Collected at counter"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setPayTarget(null);
                setPayAmount("");
                setPayNotes("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-emerald-700 hover:bg-emerald-800"
              disabled={markPaidMut.isPending}
              onClick={submitMarkPaid}
            >
              {markPaidMut.isPending ? "Recording…" : "Record payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
